import { useEffect, useCallback, useRef, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Container from '@zenbusiness/zui/Container';
import SharedLottieAsset from '@zenbusiness/zui/SharedLottieAsset';

import Transition from '#client/components/Transition';
import { STANDALONE_THREAD_STATUS } from '#client/components/utils/constants';
import { getWarmStartConfig } from '#client/constants/warmStartMessages';
import { useInstructionRenderer } from '#client/hooks/useInstructionRenderer';
import { useThreadContext } from '#client/hooks/useThreadContext';
import { useVisitorThreadCreation } from '#client/hooks/useVisitorThreadCreation';

import DynamicRenderer from '../components/DynamicRenderer';
import LegalFooter from '../components/LegalFooter';
import StandaloneHeader from '../components/StandaloneHeader';

// Default values used as fallback for unmapped URL segments
const DEFAULT_AGENT_NAME = 'business-name-generation';

/**
 * Maps URL path segments to their corresponding backend agent names.
 *
 * @description
 * This mapping allows the component to dynamically route to different AI agents
 * based on the current URL. Each URL segment corresponds to a specific agent
 * that handles different business name generation workflows.
 */
const URL_TO_AGENT_MAP = {
  'business-name': DEFAULT_AGENT_NAME,
  'business-name-research': 'business-name-research'
};

const DEFAULT_PERSIST_KEY = 'business-name-generation';

// Map URL segments to their corresponding persist keys
const URL_TO_PERSIST_KEY_MAP = {
  'business-name': DEFAULT_PERSIST_KEY,
  'business-name-research': 'business-name-research-state'
};

const DEFAULT_HEADER = {
  title: 'Business Name Generator',
  subtitle:
    'Discover the perfect name for your business with AI-powered suggestions. Complete with domain availability and trademark searches.'
};

// Map URL segments to their corresponding page titles and subtitles
const URL_TO_HEADER_MAP = {
  'business-name': DEFAULT_HEADER,
  'business-name-research': {
    title: 'Business Name Researcher',
    subtitle:
      'Determine if there are any marketing concerns with your business name. Complete with domain availability and trademark searches.'
  }
};

const userSelectionToString = (obj) => {
  return Object.entries(obj)
    .map(([key, value]) => {
      if (value instanceof Object) {
        if (value.selectedId === 'other') {
          return `${key}: ${value.inputValue}`;
        }
      }

      return `${key}: ${value?.value || value}`;
    })
    .join('. ');
};

const BASE_PROMPT = 'generate more business names based on these options: ';

const LOADER_ICON = (
  <SharedLottieAsset height="20rem" src="/s2/animation/lottie/velo-avatar-loading-light.json" />
);

// Helper functions for thread state management
const getStoredThreadState = (persistKey) => {
  try {
    const stored = localStorage.getItem(persistKey);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('[BusinessNameVisitor] Failed to parse thread state from localStorage:', error);
    return null;
  }
};

const setStoredThreadState = (persistKey, threadUuid, status) => {
  try {
    localStorage.setItem(persistKey, JSON.stringify({ threadUuid, status }));
  } catch (error) {
    console.warn('[BusinessNameVisitor] Failed to store thread state in localStorage:', error);
  }
};

export default function BusinessNameVisitor() {
  const { visitUuid, visitProcessed } = useVisit();
  const navigate = useNavigate();

  const {
    loading: threadLoading,
    error: threadError,
    createNewThread,
    retryCount
  } = useVisitorThreadCreation();

  // Use the existing instruction renderer with the threadUuid from URL
  const {
    error: instructionError,
    instructions,
    isError: isInstructionError,
    loadingNext,
    loadNextInstruction,
    loadExistingMessages,
    resetStateForNewThread,
    setInstructions
  } = useInstructionRenderer();

  const getLastUrlSegment = useCallback(() => {
    const pathSegments = window.location.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  }, []);

  // Dynamic persist keys based on URL segment
  const persistKey = useMemo(() => {
    const urlSegment = getLastUrlSegment();
    return URL_TO_PERSIST_KEY_MAP[urlSegment] || DEFAULT_PERSIST_KEY;
  }, [getLastUrlSegment]);

  const formPersistKey = useMemo(() => `${persistKey}_form`, [persistKey]);
  const flowPersistKey = useMemo(() => `${persistKey}_flow`, [persistKey]);

  // Dynamic header content based on URL segment
  const headerContent = useMemo(() => {
    const urlSegment = getLastUrlSegment();
    return URL_TO_HEADER_MAP[urlSegment] || DEFAULT_HEADER;
  }, [getLastUrlSegment]);

  // Conversation context metadata (sent for all standalone tools)
  const conversationContext = useThreadContext({ persistKey });

  // Derive the report agent name dynamically from the URL segment
  const getReportAgentName = useCallback(() => {
    const urlSegment = getLastUrlSegment();

    // Look up the agent name from the URL-to-agent mapping
    const agentName = URL_TO_AGENT_MAP[urlSegment];

    // Fallback to default if URL segment not found in map
    return agentName || DEFAULT_AGENT_NAME;
  }, [getLastUrlSegment]);

  const createBusinessNameThread = useCallback(
    async (visitUuidParam) => {
      // Check for existing in-progress thread
      const storedThreadState = getStoredThreadState(persistKey);
      if (
        storedThreadState &&
        storedThreadState.status === STANDALONE_THREAD_STATUS.in_progress &&
        storedThreadState.threadUuid
      ) {
        // Resume from existing thread instead of creating a new one
        resetStateForNewThread(storedThreadState.threadUuid);
        loadExistingMessages(
          { visitUuid: visitUuidParam },
          storedThreadState.threadUuid,
          'standalone'
        );
        // Note: Instructions will be loaded by the instruction renderer
        return storedThreadState.threadUuid;
      }

      // Create thread with warm start input if available
      const initialInput = getWarmStartConfig(getLastUrlSegment());
      const businessNameInput = {
        visitUuid: visitUuidParam,
        ...initialInput
      };
      const { threadUuid: newThreadUuid, data } = await createNewThread(
        visitUuidParam,
        { ...businessNameInput, ...conversationContext },
        { redirect: false }
      );

      // Store thread state properly in localStorage
      resetStateForNewThread(newThreadUuid);
      setStoredThreadState(persistKey, newThreadUuid, STANDALONE_THREAD_STATUS.in_progress);
      setInstructions(data);
      return newThreadUuid;
    },
    [
      conversationContext,
      createNewThread,
      getLastUrlSegment,
      resetStateForNewThread,
      setInstructions,
      loadExistingMessages,
      persistKey
    ]
  );

  // Add ref to track if thread creation has been initiated to prevent double execution
  const threadCreationInitiated = useRef(false);

  // Reset thread creation flag when visitUuid changes
  useEffect(() => {
    threadCreationInitiated.current = false;
  }, [visitUuid]);

  // Remove thread state if it is not in progress on mount
  useEffect(() => {
    const storedThreadState = getStoredThreadState(persistKey);
    if (
      storedThreadState &&
      storedThreadState.status !== STANDALONE_THREAD_STATUS.in_progress &&
      !threadCreationInitiated.current
    ) {
      localStorage.removeItem(persistKey);
      localStorage.removeItem(flowPersistKey);
      localStorage.removeItem(formPersistKey);
    }
  }, [persistKey, flowPersistKey, formPersistKey]);

  // Auto-create thread on mount
  useEffect(() => {
    // Since we have a cronjob that removes inactive threads, we need to check if the thread is still valid
    const threadNotFound = isInstructionError && instructionError?.errorType === 'INVALID_THREAD';
    if (threadNotFound) {
      threadCreationInitiated.current = false;
      localStorage.removeItem(persistKey);
      localStorage.removeItem(flowPersistKey);
      localStorage.removeItem(formPersistKey);
    }

    // Prevent double execution by checking if thread creation has already been initiated
    if (!threadLoading && !threadError && visitUuid && !threadCreationInitiated.current) {
      createBusinessNameThread(visitUuid).then((newThreadUuid) => {
        if (newThreadUuid) {
          threadCreationInitiated.current = true;
        }
      });
    }
  }, [
    createBusinessNameThread,
    threadLoading,
    threadError,
    visitUuid,
    isInstructionError,
    instructionError?.errorType,
    persistKey,
    flowPersistKey,
    formPersistKey
  ]);

  const clearStoredThreadState = useCallback(() => {
    localStorage.removeItem(persistKey);
  }, [persistKey]);

  const handleComponentClick = useCallback(
    async (action) => {
      const userProfile = action?.payload?.answers || {};
      const reportAgentName = getReportAgentName();

      // Update form state when choice flow is submitted
      if (action.type === 'choice_flow_submission') {
        await loadNextInstruction(userSelectionToString(userProfile), 'invoke', 'standalone', {
          visitUuid,
          agentName: reportAgentName,
          ...conversationContext
        });
        const storedThreadState = getStoredThreadState(persistKey);
        setStoredThreadState(
          persistKey,
          storedThreadState.threadUuid,
          STANDALONE_THREAD_STATUS.complete
        );
      }

      if (action.type === 'button_click') {
        await loadNextInstruction(
          `${BASE_PROMPT} ${userSelectionToString(userProfile)}`,
          'invoke',
          'standalone',
          {
            visitUuid,
            agentName: reportAgentName,
            ...conversationContext
          }
        );
      }

      if (action.type === 'business_name_results_start_over') {
        localStorage.removeItem(persistKey);
        localStorage.removeItem(flowPersistKey);
        localStorage.removeItem(formPersistKey);
        // Clear thread state to allow creating a new thread
        clearStoredThreadState();
        // refresh the page
        navigate(0);
      }
    },
    [
      clearStoredThreadState,
      conversationContext,
      loadNextInstruction,
      navigate,
      visitUuid,
      getReportAgentName,
      persistKey,
      flowPersistKey,
      formPersistKey
    ]
  );

  if (!visitProcessed) {
    return <Transition icon={LOADER_ICON} type="VISIT_LOADING" />;
  }

  // Show thread creation loading state
  if (threadLoading) {
    return (
      <Transition
        icon={LOADER_ICON}
        type={retryCount > 0 ? 'THREAD_CREATING_RETRY' : 'THREAD_CREATING'}
        typeProps={{ retryCount }}
      />
    );
  }

  // Determine content to show based on loading state
  let content;

  if (loadingNext && threadCreationInitiated.current) {
    content = <Transition loadingVariant="tools" type="BUSINESS_NAME_GENERATING" />;
  } else if (loadingNext && !threadCreationInitiated.current) {
    content = <Transition loadingVariant="tools" type="BUSINESS_NAME_LOADING" />;
  } else {
    content = (
      <DynamicRenderer
        currentMode="initial"
        fullWidth
        instructions={instructions}
        isChatMode={false}
        onComponentClick={handleComponentClick}
      />
    );
  }

  // Note: CustomFormProvider is now at app root (Providers.js)
  // ChoiceFlow registers its persistKey dynamically when it mounts
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <StandaloneHeader subtitle={headerContent.subtitle} title={headerContent.title} />
      <Container>
        {content}
        <Box
          display="flex"
          justifyContent="center"
          sx={(theme) => ({
            width: '100%',
            margin: 'auto',
            paddingBlock: theme.spacing(1.5)
          })}
        >
          <LegalFooter />
        </Box>
      </Container>
    </Box>
  );
}
